/**
 * SkillCard — Cloudscape Popover showing skills for a category.
 *
 * The trigger is the pack avatar icon. Clicking a chip fills the
 * input box and programmatically closes the popover via a key trick:
 * we remount the Popover by toggling a React key, which resets its
 * internal open state.
 */

import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { Popover } from '@amzn/awsui-components-react';
import type { CatalogItem } from '../../client/SkillClient';
import type zhChat from '../../i18n/locales/zh/chat';
import './SkillCard.css';

type PackDescriptionKey = keyof (typeof zhChat)['pack']['descriptions'];

/** Generic avatar for a category without its own artwork. */
// Same style as campaign-copilot.png (flat glyph on a 160x160 light square), in purple.
const DEFAULT_AVATAR = '/icons/avatar-pack-default.svg';

/**
 * Keyed by the category label (utils/skillCategories CATEGORIES[].label). Names are
 * brands and are not translated; `description` is a chat:pack.descriptions key.
 */
const CATEGORY_META: Record<string, { avatar: string; label: string; description?: PackDescriptionKey }> = {
  DataRetriever: {
    avatar: '/icons/avatar-pack-dog.png',
    label: 'DataRetriever',
    description: 'DataRetriever',
  },
  Hedwig: {
    avatar: '/icons/avatar-pack-owl.png',
    label: 'Hedwig',
    description: 'Hedwig',
  },
  Cheetah: {
    avatar: '/icons/cheetah.png',
    label: 'Cheetah',
    description: 'Cheetah',
  },
  IVT: {
    avatar: '/icons/ivt.png',
    label: 'IVT',
    description: 'IVT',
  },
  'Campaign Copilot': {
    // Octopus logo (prod.cdn.octopus.marketing.aws.a2z.org.cn/website/images/favicon.png),
    // bundled and centred on a 160x160 light square like the rocky pack avatars.
    avatar: '/icons/campaign-copilot.png',
    label: 'Campaign Copilot',
    description: 'CampaignCopilot',
  },
  'Event Insights': {
    avatar: DEFAULT_AVATAR,
    label: 'Event Insights',
    description: 'EventInsights',
  },
};

interface SkillCardProps {
  category: string;
  skills: CatalogItem[];
  onSelectSkill: (displayName: string) => void;
  /** When true, the trigger icon is non-interactive (e.g. during streaming) */
  disabled?: boolean;
}

export default function SkillCard({ category, skills, onSelectSkill, disabled }: SkillCardProps) {
  const { t } = useTranslation('chat');
  // Also the fallback for a category not in CATEGORY_META (it used to point at a
  // non-existent avatar-pack-default.png).
  const meta = CATEGORY_META[category] ?? { avatar: DEFAULT_AVATAR, label: category };
  const description = meta.description ? t(`pack.descriptions.${meta.description}`) : '';

  // Toggling the key forces Popover to remount → closes it
  const [popoverKey, setPopoverKey] = useState(0);

  const handleChipClick = useCallback((name: string) => {
    onSelectSkill(name);
    setPopoverKey(k => k + 1); // remount = close
  }, [onSelectSkill]);

  // Disabled state: render a non-interactive avatar (same style as "no skills loaded")
  if (disabled) {
    return (
      <span className="skill-card__trigger skill-card__trigger--disabled" title={meta.label} aria-label={t('pack.unavailable', { label: meta.label })}>
        <img src={meta.avatar} alt={meta.label} className="skill-card__trigger-img" />
      </span>
    );
  }

  return (
    <Popover
      key={popoverKey}
      position="top"
      size="large"
      triggerType="custom"
      renderWithPortal
      dismissButton={false}
      content={
        <>
          {/* Header: avatar + text */}
          <div className="skill-card__header">
            <div className="skill-card__avatar">
              <img src={meta.avatar} alt={meta.label} className="skill-card__avatar-img" />
            </div>
            <div className="skill-card__header-text">
              <div className="skill-card__title">{meta.label}</div>
              {description && <div className="skill-card__desc">{description}</div>}
            </div>
          </div>

          {/* Skill chips */}
          <div className="skill-card__chips">
            {skills.map((skill) => {
              const name = skill.displayName ?? skill.skillName ?? '';
              return (
                <button
                  key={skill.skillName}
                  className="skill-card__chip"
                  onClick={() => handleChipClick(name)}
                  title={name}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </>
      }
    >
      <span className="skill-card__trigger" title={meta.label} aria-label={t('pack.open', { label: meta.label })}>
        <img src={meta.avatar} alt={meta.label} className="skill-card__trigger-img" />
      </span>
    </Popover>
  );
}
